/**
 * The client's entry point ("/").
 *
 * Opening a link used to cost three steps before the first photo: the link
 * opened on a full-screen brief, the client read it, then tapped "Start
 * picking" to reach the gallery. The brief was never the thing they came for.
 *
 * The gallery now renders straight away and the photographer's brief rides on
 * top as a dismissible overlay — same words, same target, but the photos are
 * already there behind it. Dismissal is remembered in `state/linkVisit` per
 * share token, so coming back from a photo doesn't replay it while a second
 * link opened in the same tab still gets its own brief.
 *
 * Rendering the gallery this early does NOT mean the client viewed it: the
 * studio's `gallery_viewed` engagement signal waits until the brief is gone
 * (see `Gallery`'s `briefVisible` prop).
 *
 * The passcode gate is untouched: it lives in App's ManifestBoundary and still
 * runs before this route ever mounts.
 */

import { useCallback, useMemo, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Target, X } from 'lucide-react'
import { useManifest } from '../state/manifest'
import { clientPolicyForManifest } from '../state/capabilities'
import { hasSeenBrief, markBriefSeen } from '../state/linkVisit'
import { useKeyboard } from '../hooks/useKeyboard'
import { effectiveReviewTarget } from '../utils/reviewTarget'
import { ShotSelectLogo } from '../components/ShotSelectLogo'
import { Gallery } from './Gallery'

export function Landing() {
  const manifest = useManifest((s) => s.manifest)
  const shareToken = useManifest((s) => s.shareToken)
  // `shareToken` is subscribed, not frozen: App re-loads the manifest on every
  // location change, so a second link can arrive while this route stays
  // mounted. Re-read the dismissal when the token moves — a useState
  // initialiser runs once and would keep answering for the previous link, so
  // the second link's brief would never show.
  const [briefToken, setBriefToken] = useState(shareToken)
  const [dismissed, setDismissed] = useState(() => hasSeenBrief(shareToken))
  if (briefToken !== shareToken) {
    setBriefToken(shareToken)
    setDismissed(hasSeenBrief(shareToken))
  }

  const dismiss = useCallback(() => {
    setDismissed(true)
    markBriefSeen(shareToken)
  }, [shareToken])

  if (!manifest) return null

  // An empty gallery has no brief to give: the gallery's own "nothing shared
  // yet" state says the only thing there is to say.
  const showBrief = !dismissed && manifest.photos.length > 0

  // The gallery is live behind the brief but not yet *viewed* — it only counts
  // as a view for the studio once the note is out of the way.
  return (
    <>
      <Gallery briefVisible={showBrief} />
      {showBrief && <WelcomeBrief onDismiss={dismiss} />}
    </>
  )
}

/**
 * The photographer's brief, unchanged in substance — what they curated, what
 * the client is asked to do, and the album target. Only its position changed:
 * over the gallery instead of in front of it.
 */
function WelcomeBrief({ onDismiss }: { onDismiss: () => void }) {
  const manifest = useManifest((s) => s.manifest)
  const reduced = useReducedMotion()
  // Bound here rather than on the route so Escape stops being live the moment
  // the note is gone.
  useKeyboard(useMemo(() => ({ Escape: onDismiss }), [onDismiss]))
  if (!manifest) return null

  const policy = clientPolicyForManifest(manifest)
  const reviewEnabled = policy.can.select
  const downloadsEnabled =
    policy.downloads.prepared.length > 0 || policy.downloads.originals.length > 0
  const downloadCopy =
    policy.downloads.prepared.length === 1 &&
    policy.downloads.prepared[0] === 'web' &&
    policy.downloads.originals.length === 0
      ? ' or download the web-size file.'
      : downloadsEnabled
        ? ' or download an available file.'
        : '.'
  const photoCount = manifest.photos.length
  const duration = reduced ? 0 : 0.18
  // Clamped against the actual photo count — a quota of 40-60 on a 12-photo
  // link would otherwise ask the client for more selects than exist.
  const reviewTarget = effectiveReviewTarget(manifest.quotaTarget, photoCount)

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <motion.div
        className="absolute inset-0 bg-black/60"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration }}
        onClick={onDismiss}
        aria-hidden
      />
      <motion.div
        data-testid="welcome-brief"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-brief-title"
        initial={{ opacity: 0, y: reduced ? 0 : 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration }}
        className="relative w-full max-w-[440px] rounded-t-3xl border border-[var(--border-default)] bg-[var(--surface-primary)] px-6 pb-7 pt-3 sm:rounded-3xl sm:pt-5"
        style={{
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          paddingBottom: 'max(1.75rem, env(safe-area-inset-bottom))',
        }}
      >
        {/* Grab handle · the sheet affordance a phone expects. Gone once the
            same card is a centred dialog from sm up. */}
        <div
          aria-hidden
          className="mx-auto mb-3 h-1 w-9 rounded-full bg-[var(--border-strong)] sm:hidden"
        />
        <div className="flex items-start gap-3">
          {manifest.photographer.brandLogoUrl ? (
            <img
              src={manifest.photographer.brandLogoUrl}
              className="h-7 w-7 rounded-lg border border-[var(--border-default)] bg-[var(--surface-tertiary)] object-contain"
              alt=""
            />
          ) : (
            <div className="grid h-7 w-7 place-items-center rounded-lg border border-[var(--border-default)] bg-[var(--surface-tertiary)] text-[var(--text-primary)]">
              <ShotSelectLogo className="h-4 w-4" />
            </div>
          )}
          <div className="min-w-0 flex-1 leading-tight">
            <div id="welcome-brief-title" className="text-[13px] font-semibold">
              {manifest.photographer.name}
            </div>
            <div className="text-[12px] uppercase tracking-wider text-[var(--text-tertiary)]">
              Curated for you
            </div>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss this note"
            data-testid="welcome-brief-close"
            className="-mr-2 -mt-2 grid h-11 w-11 shrink-0 place-items-center rounded-full text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
          >
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">
          {reviewEnabled
            ? `I've curated ${photoCount} photos for you. Select the ones you want, leave undecided ones for later, or reject the rest. Your choices come straight back to me — no email tag.`
            : `I've shared ${photoCount} finished photos for you. Open any image to view it larger${downloadCopy}`}
        </p>

        {reviewEnabled && reviewTarget && (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-secondary)] px-3 py-2.5 text-xs">
            <Target size={14} strokeWidth={2} className="shrink-0 text-[var(--text-secondary)]" />
            <span className="font-semibold text-[var(--text-primary)]">
              Aim for {reviewTarget.min}–{reviewTarget.max}
            </span>
            <span className="text-[var(--text-tertiary)]">· for the album</span>
          </div>
        )}

        {/* --accent-primary inverts between the dark shell and the light gallery
            underneath; pairing text/surface keeps the label readable in both
            instead of dark-on-dark once the gallery flips the theme. */}
        <button
          type="button"
          autoFocus
          onClick={onDismiss}
          data-testid="welcome-brief-start"
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--text-primary)] text-sm font-semibold text-[var(--surface-primary)] transition-transform active:scale-[0.99]"
          style={{ height: '52px' }}
        >
          {reviewEnabled ? 'Start picking' : 'View gallery'}
          <ArrowRight size={16} strokeWidth={2.2} />
        </button>

        {reviewEnabled && (
          <div className="mt-2.5 text-center text-[12px] text-[var(--text-tertiary)]">
            ~15 minutes · resume on any device · works offline
          </div>
        )}
      </motion.div>
    </div>
  )
}
